import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PermissionService, FeatureName } from '../../../@backoffice/services/permission.service';
import { LoginService } from '../../auth/login/login.service';
import { UserService } from '../../../@backoffice/services/user.service';
import { buildClientRoute } from '../utils/client-route.util';

/**
 * Permission Guard
 * 
 * This guard protects routes by checking if the user has the required permission.
 * If the user doesn't have the required permission, it redirects to the access denied page.
 * 
 * Handles page refresh by fetching fresh user info when permissions are not yet initialized.
 * 
 * Usage: Add `canActivate: [permissionGuard('tenant', 'read')]` to any route that requires permission.
 * 
 * @param featureName - The feature name to check permission for
 * @param requiredPermission - 'read', 'edit', or 'readAndEdit' (both required; used for member detail tabs)
 */
export const permissionGuard = (
  featureName: FeatureName | string | ReadonlyArray<FeatureName | string>,
  requiredPermission: 'read' | 'edit' | 'readAndEdit' = 'read'
): CanActivateFn => {
  return (route, state) => {
    const permissionService = inject(PermissionService);
    const router = inject(Router);
    const loginService = inject(LoginService);

    // A list means any one of the features is enough (e.g. API Key: its own permission or API Users').
    const features = Array.isArray(featureName) ? featureName : [featureName as FeatureName | string];
    const checkPermission = (): boolean => features.some(feature => {
      if (requiredPermission === 'edit') {
        return permissionService.hasEditPermission(feature);
      }
      if (requiredPermission === 'readAndEdit') {
        return permissionService.hasReadAndEditPermission(feature);
      }
      return permissionService.hasReadPermission(feature);
    });

    // Check if permissions are already initialized
    const allPermissions = permissionService.getAllPermissions();
    if (allPermissions) {
      if (checkPermission()) {
        return true;
      }

      console.warn(`Permission denied for feature: ${featureName}, required: ${requiredPermission}`);
      router.navigate(buildClientRoute(router, ['/access-denied']));
      return false;
    }

    // Permissions not ready: fetch fresh user details so permissions can be initialized
    const storedUserInfo = loginService.getUserInfo() as { nameIdentifier?: string } | null;
    if (!storedUserInfo?.nameIdentifier) {
      router.navigate(buildClientRoute(router, ['/login']));
      return false;
    }

    return permissionService.ensurePermissions(storedUserInfo.nameIdentifier).pipe(
      map(() => {
        if (checkPermission()) {
          return true;
        }

        router.navigate(buildClientRoute(router, ['/access-denied']));
        return false;
      }),
      catchError((error) => {
        console.error('PermissionGuard: Error fetching user details for permission check', error);
        // On auth errors or other failures, route to login or access denied accordingly
        const statusCode = (error?.status ?? error?.statusCode) as number | undefined;
        if (statusCode === 401) {
          router.navigate(buildClientRoute(router, ['/login']));
        } else {
          router.navigate(buildClientRoute(router, ['/access-denied']));
        }
        return of(false);
      })
    );
  };
};
